import { useMemo } from "react";
import { PlanSegment, ProjectParticipant, ProjectPlanProject } from "../models/project-plan-types";

/**
 * Проекты, готовые к отрисовке: отфильтрованные поиском и обрезанные по видимой сетке.
 *
 * Поиск двухуровневый. Если запрос попал в сам проект (номер, название, номер
 * договора, ГИП) — показываем его целиком. Если только в участников — оставляем в проекте
 * лишь совпавшие строки: искали человека, значит и раскрыть проект надо на нём,
 * а не на всём отделе, который рядом с ним работает.
 *
 * При этом заголовок проекта фильтр не режет: сводная полоса и крайний день
 * приходят с сервера, посчитанные по ВСЕМ участникам — в том числе по тем, кого
 * не показывают права на отдел. Срок проекта — свойство проекта, а не выборки,
 * и по нему должно быть видно, где внутри него лежат найденные работы.
 *
 * Обрезка по сетке обязательна. Отрезки собираются по ВСЕМ загруженным колонкам
 * (с сервера приходит максимум обоих масштабов), а сетка рисует только первые
 * `columnsCount` из них. Индекс за этой границей `columnLeft` отдаёт как правый край
 * последней колонки — полоса схлопнулась бы в артефакт у края вместо того, чтобы исчезнуть.
 *
 * Считается один раз на изменение данных, поиска или глубины показа — в рендере
 * полос остаётся только чтение готовых чисел.
 */
export function useVisibleProjects(
    projects: ProjectPlanProject[],
    query: string,
    columnsCount: number
): ProjectPlanProject[] {
    return useMemo(() => {
        const result: ProjectPlanProject[] = [];

        projects.forEach((project) => {
            const wholeProject = !query || matchesProject(project, query);

            const shown: ProjectParticipant[] = [];

            project.rows.forEach((row) => {
                if (!wholeProject && !matchesRow(row, query)) {
                    return;
                }

                const segments = clipAll(row.segments, columnsCount);

                if (segments.length) {
                    shown.push({ ...row, segments });
                }
            });

            if (!shown.length) {
                return;
            }

            const summary = project.summary ? clipAll([project.summary], columnsCount) : [];

            result.push({ ...project, rows: shown, summary: summary[0] || null });
        });

        return result;
    }, [projects, query, columnsCount]);
}

function matchesProject(project: ProjectPlanProject, query: string): boolean {
    // по ГИПу ищут так же часто, как по номеру: «что сейчас у Иванова»
    const haystack = `${project.fullInventoryNumber} ${project.name} ${project.orderNumber} ${project.employer}`.toLowerCase();

    return haystack.indexOf(query) >= 0;
}

function matchesRow(row: ProjectParticipant, query: string): boolean {
    return `${row.label} ${row.note}`.toLowerCase().indexOf(query) >= 0;
}

function clipAll(segments: PlanSegment[], columnsCount: number): PlanSegment[] {
    const result: PlanSegment[] = [];

    segments.forEach((segment) => {
        const last = segment.start + segment.length - 1;
        const end = Math.min(last, columnsCount - 1);

        if (segment.start <= end) {
            result.push({
                ...segment,
                length: end - segment.start + 1,
                // сетка обрезала отрезок сильнее, чем бэк — полоса всё равно уходит за край
                continuesRight: segment.continuesRight || end < last,
            });
        }
    });

    return result;
}
